import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './AgentStatus.module.css';
import { ToolCall } from '../../services/teachingAgent';

interface AgentStatusProps {
  isThinking: boolean;
  isStreaming: boolean;
  currentTool: ToolCall | null;
  sessionId: string | null;
  error?: string | null;
}

type AgentState = 'ready' | 'thinking' | 'searching' | 'writing' | 'coding' | 'celebrating' | 'error';

const STATE_CONFIG: Record<AgentState, {
  textKey: string;
  color: string;
  animation: 'bounce' | 'spin' | 'pulse' | 'wave' | 'shake' | 'none';
}> = {
  ready: {
    textKey: 'agentStatus.state.ready',
    color: '#5cb85c',
    animation: 'none',
  },
  thinking: {
    textKey: 'agentStatus.state.thinking',
    color: '#007acc',
    animation: 'pulse',
  },
  searching: {
    textKey: 'agentStatus.state.searching',
    color: '#f0ad4e',
    animation: 'spin',
  },
  writing: {
    textKey: 'agentStatus.state.writing',
    color: '#5bc0de',
    animation: 'wave',
  },
  coding: {
    textKey: 'agentStatus.state.coding',
    color: '#9b59b6',
    animation: 'bounce',
  },
  celebrating: {
    textKey: 'agentStatus.state.celebrating',
    color: '#f1c40f',
    animation: 'bounce',
  },
  error: {
    textKey: 'agentStatus.state.error',
    color: '#d9534f',
    animation: 'shake',
  },
};

const TOOL_STATES: Record<string, {
  state: AgentState;
  textKey: string;
}> = {
  'ask_socratic_question': { state: 'thinking', textKey: 'tutor.status.ask_socratic_question' },
  'show_video': { state: 'searching', textKey: 'tutor.status.show_video' },
  'show_book_excerpt': { state: 'searching', textKey: 'tutor.status.show_book_excerpt' },
  'celebrate': { state: 'celebrating', textKey: 'tutor.status.celebrate' },
  'provide_hint': { state: 'thinking', textKey: 'tutor.status.provide_hint' },
  'assess_understanding': { state: 'thinking', textKey: 'tutor.status.assess_understanding' },
  'demonstrate_code': { state: 'coding', textKey: 'tutor.status.demonstrate_code' },
  'run_code': { state: 'coding', textKey: 'tutor.status.run_code' },
  'explain_concept': { state: 'writing', textKey: 'tutor.status.explain_concept' },
};

// Thinking dots animation
const ThinkingDots: React.FC = () => (
  <span className={styles.thinkingDots}>
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className={styles.dot}
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{
          duration: 1,
          repeat: Infinity,
          delay: i * 0.2,
        }}
      >
        .
      </motion.span>
    ))}
  </span>
);

// Progress indicator for long operations
const ProgressIndicator: React.FC<{ state: AgentState }> = ({ state }) => {
  const config = STATE_CONFIG[state];

  return (
    <motion.div
      className={styles.progressIndicator}
      initial={{ width: 0 }}
      animate={{ width: '100%' }}
      transition={{ duration: 10, ease: 'linear' }}
      style={{ backgroundColor: config.color }}
    />
  );
};

const AgentStatus: React.FC<AgentStatusProps> = ({
  isThinking,
  isStreaming,
  currentTool,
  sessionId,
  error,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [messageCount, setMessageCount] = useState(0);

  // Determine current state
  const getCurrentState = (): AgentState => {
    if (error) return 'error';
    if (currentTool) {
      const toolState = TOOL_STATES[currentTool.name];
      return toolState?.state || 'thinking';
    }
    if (isThinking) return 'thinking';
    if (isStreaming) return 'writing';
    return 'ready';
  };

  const currentState = getCurrentState();
  const config = STATE_CONFIG[currentState];

  // Get status text
  const getStatusText = (): string => {
    if (error) return error;
    if (currentTool) {
      const toolKey = TOOL_STATES[currentTool.name]?.textKey;
      return toolKey ? t(toolKey) : t('tutor.status.generic');
    }
    return t(config.textKey);
  };

  // Track message count for session
  useEffect(() => {
    if (isStreaming) {
      setMessageCount((prev) => prev + 1);
    }
  }, [isStreaming]);

  const isActive = currentState !== 'ready';

  // Animation variants for the icon
  const iconVariants = {
    bounce: {
      y: [0, -4, 0],
      transition: { duration: 0.5, repeat: Infinity },
    },
    spin: {
      rotate: 360,
      transition: { duration: 1, repeat: Infinity, ease: 'linear' as const },
    },
    pulse: {
      scale: [1, 1.2, 1],
      transition: { duration: 1, repeat: Infinity },
    },
    wave: {
      rotate: [0, 14, -8, 14, -4, 10, 0],
      transition: { duration: 1.5, repeat: Infinity },
    },
    shake: {
      x: [-2, 2, -2, 2, 0],
      transition: { duration: 0.4, repeat: Infinity, repeatDelay: 1 },
    },
    none: {},
  };

  return (
    <motion.div
      className={`${styles.statusBar} ${isActive ? styles.active : ''} ${error ? styles.error : ''}`}
      animate={{
        backgroundColor: isActive
          ? `${config.color}15`
          : 'var(--bg-tertiary, #252526)',
      }}
      transition={{ duration: 0.3 }}
      onClick={() => setShowDetails(!showDetails)}
      role={error ? 'alert' : 'status'}
    >
      {/* Progress bar for active states */}
      <AnimatePresence>
        {isActive && currentState !== 'error' && (
          <ProgressIndicator state={currentState} />
        )}
      </AnimatePresence>

      <div className={styles.statusContent}>
        {/* Animated Icon */}
        <motion.span
          className={styles.statusIcon}
          variants={iconVariants}
          animate={config.animation}
          style={{ color: config.color }}
        >
          <span className={styles.statusDot} aria-hidden style={{ backgroundColor: config.color }} />
        </motion.span>

        {/* Status Text */}
        <AnimatePresence mode="wait">
          <motion.span
            key={getStatusText()}
            className={styles.statusText}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {getStatusText()}
            {(isThinking || (currentTool && currentState !== 'ready')) && <ThinkingDots />}
          </motion.span>
        </AnimatePresence>
      </div>

      {/* Right side info */}
      <div className={styles.rightInfo}>
        {/* Tool badge */}
        <AnimatePresence>
          {currentTool && (
            <motion.div
              className={styles.toolBadge}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              style={{ borderColor: config.color }}
            >
              {TOOL_STATES[currentTool.name]?.state === 'coding' ? '{ }' : null}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Session info */}
        {sessionId && (
          <div className={styles.sessionInfo}>
            <motion.span
              className={styles.sessionDot}
              animate={{
                backgroundColor: isActive ? config.color : '#5cb85c',
                scale: isActive ? [1, 1.2, 1] : 1,
              }}
              transition={{ duration: 1, repeat: isActive ? Infinity : 0 }}
            />
            <span className={styles.sessionId}>
              {messageCount > 0 && (
                <span className={styles.messageCount}>{t('agentStatus.messages_short', { count: messageCount })}</span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {showDetails && sessionId && (
          <motion.div
            className={styles.detailsPanel}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className={styles.detailRow}>
              <span>{t('agentStatus.session_id')}</span>
              <code>{sessionId}</code>
            </div>
            <div className={styles.detailRow}>
              <span>{t('agentStatus.messages')}</span>
              <span>{messageCount}</span>
            </div>
            <div className={styles.detailRow}>
              <span>{t('agentStatus.status')}</span>
              <span style={{ color: config.color }}>{t(config.textKey)}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AgentStatus;
